import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Transaction {
  id: string;
  data: string;
  tip: 'venit' | 'cheltuiala';
  categorie: string;
  descriere: string;
  suma: number;
  parohieId?: string;
  status: 'confirmat' | 'in_asteptare' | 'anulat';
}

const Tranzactii: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTip, setFilterTip] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Mock data pentru tranzacții
  const [transactions] = useState<Transaction[]>([
    {
      id: '1',
      data: '2024-01-15',
      tip: 'venit',
      categorie: 'Donatii',
      descriere: 'Donatie de la credincioși',
      suma: 2500,
      parohieId: 'parohia1',
      status: 'confirmat'
    },
    {
      id: '2',
      data: '2024-01-16',
      tip: 'cheltuiala',
      categorie: 'Utilitati',
      descriere: 'Factura electricitate',
      suma: 450,
      parohieId: 'parohia1',
      status: 'confirmat'
    },
    {
      id: '3',
      data: '2024-01-17',
      tip: 'venit',
      categorie: 'Subventii',
      descriere: 'Subventie pentru renovare',
      suma: 10000,
      parohieId: 'parohia2',
      status: 'in_asteptare'
    },
    {
      id: '4',
      data: '2024-01-18',
      tip: 'cheltuiala',
      categorie: 'Renovari',
      descriere: 'Materiale pentru renovare',
      suma: 3500,
      parohieId: 'parohia2',
      status: 'confirmat'
    }
  ]);

  const transactionCategories = ['Donatii', 'Subventii', 'Utilitati', 'Renovari', 'Salarii', 'Altele'];

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.descriere.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.categorie.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterTip || transaction.tip === filterTip;
    const matchesCategory = !filterCategorie || transaction.categorie === filterCategorie;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const totalVenituri = transactions
    .filter(t => t.tip === 'venit' && t.status === 'confirmat')
    .reduce((sum, t) => sum + t.suma, 0);

  const totalCheltuieli = transactions
    .filter(t => t.tip === 'cheltuiala' && t.status === 'confirmat')
    .reduce((sum, t) => sum + t.suma, 0);

  const sold = totalVenituri - totalCheltuieli;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmat': return 'success';
      case 'in_asteptare': return 'warning';
      case 'anulat': return 'error';
      default: return 'default';
    }
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Tranzacții
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare tranzacție nouă */}}
          >
            Tranzacție nouă
          </Button>
        )}
      </Box>

      {/* Statistici financiare */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  📈
                </Typography>
                <Typography color="textSecondary">
                  Venituri totale
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalVenituri.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  📉
                </Typography>
                <Typography color="textSecondary">
                  Cheltuieli totale
                </Typography>
              </Box>
              <Typography variant="h5" color="error.main">
                €{totalCheltuieli.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Sold
                </Typography>
              </Box>
              <Typography variant="h5" color={sold >= 0 ? 'success.main' : 'error.main'}>
                €{sold.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru tranzacții */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută tranzacții..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tip</InputLabel>
              <Select
                value={filterTip}
                onChange={(e) => setFilterTip(e.target.value)}
                label="Tip"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="venit">Venit</MenuItem>
                <MenuItem value="cheltuiala">Cheltuială</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Categorie</InputLabel>
              <Select
                value={filterCategorie}
                onChange={(e) => setFilterCategorie(e.target.value)}
                label="Categorie"
              >
                <MenuItem value="">Toate</MenuItem>
                {transactionCategories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterTip('');
                setFilterCategorie('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabel tranzacții */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Categorie</TableCell>
              <TableCell>Descriere</TableCell>
              <TableCell>Suma</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{transaction.data}</TableCell>
                <TableCell>
                  <Chip
                    label={transaction.tip}
                    color={transaction.tip === 'venit' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{transaction.categorie}</TableCell>
                <TableCell>{transaction.descriere}</TableCell>
                <TableCell>
                  <Typography
                    color={transaction.tip === 'venit' ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {transaction.tip === 'venit' ? '+' : '-'}€{transaction.suma.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.status}
                    color={getStatusColor(transaction.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewTransaction(transaction)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton color="default">
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare tranzacție */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii tranzacție
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedTransaction.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tip
                </Typography>
                <Chip
                  label={selectedTransaction.tip}
                  color={selectedTransaction.tip === 'venit' ? 'success' : 'error'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Categorie
                </Typography>
                <Typography variant="body1">
                  {selectedTransaction.categorie}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedTransaction.status}
                  color={getStatusColor(selectedTransaction.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Descriere
                </Typography>
                <Typography variant="body1">
                  {selectedTransaction.descriere}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Suma
                </Typography>
                <Typography variant="h5" color={selectedTransaction.tip === 'venit' ? 'success.main' : 'error.main'}>
                  {selectedTransaction.tip === 'venit' ? '+' : '-'}€{selectedTransaction.suma.toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tranzactii;
